import React, { useState } from 'react';
import { FaStar, FaStarHalfAlt } from "react-icons/fa";
import { TrainerData } from '../types/TrainerInfo';
import { Link, useNavigate } from 'react-router-dom';

const TrainerCard = ({ Trainer }: { Trainer: TrainerData }) => {
    const navigate = useNavigate();
    const [isNoticeExpanded, setIsNoticeExpanded] = useState(false); // State to toggle notice expansion

    const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        navigate("/newchat", { state: { Trainer } });
    };

    const renderStars = (rating: number) => {
        const stars = [];
        const MAX_STARS = 5;
        const roundedRating = Math.round(rating * 2) / 2; // Round to nearest 0.5
        const fullStars = Math.floor(roundedRating);
        const hasHalfStar = roundedRating % 1 === 0.5;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<FaStar key={`full-${i}`} className="text-yellow-300" />);
        }

        if (hasHalfStar) {
            stars.push(<FaStarHalfAlt key="half" className="text-yellow-300" />);
        }

        const totalStars = fullStars + (hasHalfStar ? 1 : 0);
        for (let i = totalStars; i < MAX_STARS; i++) {
            stars.push(<FaStar key={`empty-${i}`} className="text-gray-300" />);
        }

        return stars;
    };

    // Check if notice is long enough to warrant a "See More" button
    const isNoticeLong = Trainer.trainerInfo.notice.split(' ').length > 10; // Arbitrary word limit (e.g., ~2 lines)

    return (
        <Link
            to={`/profile/${Trainer._id}`}
            className="flex flex-col md:flex-row-reverse items-center bg-white border rounded-xl p-4 w-full max-w-[588px] h-auto overflow-hidden"
        >
            <div className="w-full md:w-1/3 h-40 rounded-lg overflow-hidden mb-3 md:mb-0 md:ml-5">
                <img
                    src={Trainer.profileImage}
                    alt="Tutor"
                    className="object-cover w-full h-full"
                />
            </div>

            <div className="w-full md:w-2/3 text-right space-y-2 flex flex-col">
                <div className="flex flex-col md:flex-row-reverse justify-between">
                    <h3 className="text-lg md:text-xl font-bold text-gray-800 truncate">
                        {Trainer.name}
                    </h3>
                    <span className="text-green-600 font-bold text-sm md:text-base">
                        {Trainer.trainerInfo.priceOfLesson} EGP/الحصة
                    </span>
                </div>

                <div className="category-container flex flex-col gap-1">
                    {Trainer.trainerInfo.category && (
                        <span className="bg-blue-100 text-blue-800 text-xs sm:text-sm font-medium px-2 py-1 rounded-full self-end">
                            {Trainer.trainerInfo.category}
                        </span>
                    )}
                    {Trainer.trainerInfo.subCategory && (
                        <span className="bg-green-100 text-green-800 text-xs sm:text-sm font-medium px-2 py-1 rounded-full self-end">
                            {Trainer.trainerInfo.subCategory}
                        </span>
                    )}
                    {Trainer.trainerInfo.type && (
                        <span
                            className={`text-xs sm:text-sm font-medium px-2 py-1 rounded-full self-end ${Trainer.trainerInfo.type.toLowerCase() === 'online'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-300 text-gray-800'
                                }`}
                        >
                            {Trainer.trainerInfo.type}
                        </span>
                    )}
                </div>

                <div className="flex items-center space-x-1 justify-end mt-1">
                    <div className="flex space-x-1">
                        {renderStars(Trainer.trainerInfo.rate)}
                    </div>
                    <span className="text-sm text-gray-400">{Trainer.trainerInfo.numOfOrders}</span>
                </div>

                <div className="flex flex-col md:flex-row-reverse justify-between gap-3 mt-2 flex-1">
                    <div className="flex flex-col items-end md:w-full">
                        <p className={`text-sm text-gray-500 break-words ${isNoticeExpanded ? '' : 'line-clamp-2'}`}>
                            {Trainer.trainerInfo.notice}
                        </p>
                        {isNoticeLong && (
                            <button
                                className="text-primary-300 text-sm hover:underline mt-1"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsNoticeExpanded(!isNoticeExpanded);
                                }}
                            >
                                {isNoticeExpanded ? 'عرض أقل' : 'عرض المزيد'}
                            </button>
                        )}
                    </div>

                    <button
                        className="bg-primary-300 text-white px-3 py-1 h-[50px] w-1/2 rounded-lg hover:bg-primary-200 transition shrink-0"
                        onClick={handleButtonClick}
                    >
                        ابعت رسالة
                    </button>
                </div>
            </div>
        </Link>
    );
};

export default TrainerCard;